import { useState } from "react";
import { CalendarClock, Loader2, PauseCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { openTopLevel } from "@/lib/topNavigation";

export const dmy = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { timeZone: "UTC" }) : "");

// What one change does, field by field (decrease / removal / pause, or cancelling a scheduled one).
const lines = (c, t) => {
  const cur = c.current || {};
  const s = c.schedule || {};
  if (c.cancelled) return [t("cart.chgCancel")];
  const out = [];
  if ("extra_employees" in s) out.push(t("cart.chgEmp", { from: cur.extra_employees ?? 0, to: s.extra_employees }));
  if (s.team_option === false) out.push(t("cart.chgTeamOff"));
  if (s.robot_trieur === false) out.push(t("cart.chgRobotOff"));
  if (s.robot_trieur === true) out.push(t("cart.chgRobotOn"));
  if (s.paused === true) out.push(t("cart.chgPause"));
  return out.length ? out : [t("cart.chgCancel")];
};

// Row badges: already scheduled (date) / paused business (kept, reactivated through a normal payment).
export const ChangeBadges = ({ b, testId }) => {
  const { t } = useI18n();
  return (
    <>
      {b.scheduled && (
        <p className="mt-2 inline-flex max-w-full items-center gap-1 break-words rounded-full bg-amber-900/40 px-2 py-0.5 text-xs font-semibold text-amber-100" data-testid={`${testId}-scheduled`}>
          <CalendarClock className="h-3.5 w-3.5 shrink-0" /> {t("cart.scheduled")} · {t("cart.effect", { date: dmy(b.scheduled.effective_at) })}
        </p>
      )}
      {b.paused && (
        <p className="mt-2 inline-flex max-w-full items-center gap-1 break-words rounded-full bg-neutral-800 px-2 py-0.5 text-xs font-semibold text-neutral-200" data-testid={`${testId}-paused`}>
          <PauseCircle className="h-3.5 w-3.5 shrink-0" /> {t("cart.paused")}{b.request?.reactivate ? ` · ${t("cart.reactivate")}` : ""}
        </p>
      )}
    </>
  );
};

// Decreases / removals / pauses of the cart: confirmed WITHOUT payment, effective at each business's own renewal.
export const ChangesBox = ({ rows, onDone }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const list = rows.filter((b) => b.changes);
  if (!list.length) return null;
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dev/billing/cart/changes/confirm", {});
      toast.success(t("cart.chgDone"));
      if (data.retour) return openTopLevel(data.retour);  // back to the mini-site app (paye=1)
    } catch (err) {
      toast.error(translateError(err, t));
    }
    setBusy(false);
    onDone();
  };
  return (
    <div className="min-w-0 space-y-3 rounded-2xl border border-amber-400/40 bg-amber-950/20 p-4" data-testid="cart-changes">
      <h3 className="flex items-center gap-2 break-words text-sm font-bold text-amber-100"><CalendarClock className="h-4 w-4 shrink-0" /> {t("cart.chgTitle")}</h3>
      <ul className="min-w-0 space-y-2 text-sm">
        {list.map((b) => (
          <li key={`${b.key_id}:${b.ref}`} className="min-w-0 break-words text-neutral-200" data-testid={`cart-change-${b.ref}`}>
            <span className="font-semibold text-white">{b.business_name || b.ref}</span>
            {" — "}{lines(b.changes, t).join(" · ")}
            <span className="block text-xs font-semibold text-amber-200" data-testid={`cart-change-${b.ref}-date`}>{t("cart.effect", { date: dmy(b.changes.effective_at) })}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-neutral-400">{t("cart.chgNote")}</p>
      <button type="button" className="btn-gold btn-sm min-h-[44px] whitespace-normal" onClick={go} disabled={busy} data-testid="cart-changes-confirm">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {t("cart.chgConfirm")}
      </button>
    </div>
  );
};
